import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { Nullable } from '@/utils/types';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import { getBuildingDetailsItems } from '@/bundles/Property/helpers/buildingDetailsHelper';
import {
  constructionStatuses,
  FilterPrimaryUseClassItems,
  getPropertyUsesForFilters,
  rentTypes
} from '@/bundles/GroupFilters/consts';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import {
  getPropertyBuildingMarketSegmentTextValues,
  getPropertyBuildingStyleTextValues,
  getPropertyBuildingUnitMixTextValues
} from '@/bundles/Property/helpers/multifamilyHelper';
import { IFilterConfigItem, IFilterItems } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { isYearRule } from '@/bundles/GroupFilters/helpers/rules';
import { FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';

export const getPropertyBuildingFiltersConfig = ({ propertyTypesWithMixedUse, type }: {
  propertyTypesWithMixedUse: IFilterItems;
  type: string;
}): IFilterConfigItem[] => {
  return [
    {
      title: 'Property Type',
      items: propertyTypesWithMixedUse,
      name: 'property_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.building,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Building Class',
      name: 'property_building_class', // NEW
      items: getBuildingDetailsItems().building_class,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Building Size',
      name: 'property_size', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Rentable Building Size',
      name: 'property_rentable_size', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Office Space',
      name: 'property_office_size', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: '# of Floors',
      name: `${type}_floors_count`, // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Typical Floor Plate',
      name: `${type}_typical_plate`, // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Tenant Type',
      name: 'property_tenant_type', // NEW
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getBuildingDetailsItems().tenant_type,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Building Dimensions Width',
      name: `${type}_building_dimensions_width`, // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Building Dimensions Depth',
      name: `${type}_building_dimensions_depth`, // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Construction Status',
      items: constructionStatuses,
      name: 'property_construction_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: i18n.t(LocalizationEnum.property_year_built) as string,
      name: 'property_year_built', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: i18n.t(LocalizationEnum.property_year_renovated) as string,
      name: 'property_year_renovated', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: 'Spec / BTS',
      name: 'property_spec_bts', // NEW
      items: getBuildingDetailsItems().specBts,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: '# of Buildings',
      name: `${type}_building_count`, // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: '# of Units',
      name: `${type}_building_units`,
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Construction Type',
      name: 'property_construction_type', // NEW
      items: getBuildingDetailsItems().construction_type,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Exterior Type',
      name: 'property_exterior_type', // NEW
      items: getBuildingDetailsItems().exterior_type,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Roof Type',
      name: 'property_roof_type', // NEW
      items: getBuildingDetailsItems().roof_type,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Roof Date',
      name: 'property_roof_date', // NEW
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Roof Age',
      name: 'property_roof_age', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Slab Thickness',
      name: 'property_slab_thickness', // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Lighting Type',
      name: 'property_lighting_type', // NEW
      items: getBuildingDetailsItems().lighting_type,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Sprinkler Type',
      name: `${type}_sprinkler_type`, // NEW
      items: getBuildingDetailsItems().sprinkler_type,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Min Clear Height',
      name: `${type}_min_clear_height`, // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Max Clear Height',
      name: `${type}_max_clear_height`, // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Security',
      name: 'property_security', // NEW
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Restroom',
      name: 'property_restroom', // NEW
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Signage',
      name: `${type}_signage`, // NEW
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Column Spacing Width',
      name: `${type}_column_width`, // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Column Spacing Depth',
      name: `${type}_column_depth`, // NEW
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Warehouse Heating',
      name: 'property_warehouse_heating', // NEW
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Warehouse Ventilation',
      name: 'property_warehouse_ventilation', // NEW
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Mezzanine Size',
      name: 'property_mezzanine_size', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Mezzanine Type',
      name: 'property_mezzanine_type', // NEW
      items: getBuildingDetailsItems().mezzanine_type,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Mezzanine Property',
      name: 'property_mezzanine_property', // NEW
      items: getBuildingDetailsItems().mezzanine_property,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Unit Mix',
      name: `${type}_unit_mix`, // NEW
      items: getPropertyBuildingUnitMixTextValues(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Style',
      name: `${type}_unit_style`, // NEW
      items: getPropertyBuildingStyleTextValues(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Commercial Space',
      name: `${type}_commercial_space`, // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Market Segment',
      name: `${type}_market_segment`, // NEW
      items: getPropertyBuildingMarketSegmentTextValues(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
    {
      title: 'Average Size Per Unit',
      name: `${type}_avg_size_per_unit`, // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.building,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Rent Type',
      name: `${type}_rent_type`, // NEW
      items: rentTypes,
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.building,
    },
  ];
}
